import React from 'react';
import qs from 'qs';
import { useUrlLocalizer, useTranslator } from 'tradera/lang/translate';
import { useInView } from 'react-intersection-observer';
import { trackContainerClick } from '../analytics';
import { Carousel } from 'tradera/components/carousel/carousel';
import type { PossibleItemTypes } from 'tradera/components/item-card/item-card';
import { ItemCard } from 'tradera/components/item-card/item-card';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import * as itemActions from 'tradera/state/items/actions';
import uiActions from 'tradera/state/ui/actions';
import { MoreResultsCard } from 'tradera/components/carousel/more-results-card/more-results-card';
import {
    LeftButton,
    RightButton
} from 'tradera/components/carousel/carousel-buttons';
import { CarouselContent } from 'tradera/components/carousel/carousel-content';
import { useGetLastSearch } from './use-get-last-search';
import { useAppDispatch } from 'tradera/state/hooks';
import { StartPageSection } from '../start-page-section/start-page-section';
import { StartPageSectionTitle } from '../start-page-section/start-page-section-title';

export const LastSearch = () => {
    const { t } = useTranslator();
    const isMobile = useIsMobile();
    const dispatch = useAppDispatch();

    const [loadingTriggerRef, loadingTriggerInView] = useInView({
        triggerOnce: true
    });

    const { queryParams, data, isLoading, isSuccess } =
        useGetLastSearch(loadingTriggerInView);

    const { items, totalItems } = data || {};

    // To indicate that the list is scrollable on mobile we show 2.5 items
    const numberCarouselCards = isMobile ? 2.5 : 6;

    const openQuickView = (item: PossibleItemTypes) => {
        dispatch(
            uiActions.openQuickViewAction({
                items,
                item,
                openedFrom: 'Start Page Last Search'
            })
        );
    };

    const { toLocalizedUrl } = useUrlLocalizer();

    const toggleWishList = (itemId: number) => {
        dispatch(itemActions.wishlist(itemId));
    };

    const moreItemsCanBeFetched =
        totalItems && items?.length && totalItems > items?.length;

    return (
        <StartPageSection ref={loadingTriggerRef}>
            {!isSuccess ||
            isLoading ||
            (isSuccess && items?.length === 0) ? null : (
                <>
                    <StartPageSectionTitle
                        href={toLocalizedUrl(
                            `/search?${qs.stringify(queryParams)}`
                        )}
                        inSpa
                        onClick={() =>
                            trackContainerClick(
                                'lastSearch',
                                'Last search button below'
                            )
                        }
                        showAll>
                        {t('startPage_lastSearch_title')}
                    </StartPageSectionTitle>

                    <Carousel
                        numberItemsToShow={numberCarouselCards}
                        numberItemsToScroll={numberCarouselCards}
                        className="mx-md-3 mx-lg-2 mx-xl-0"
                        padX>
                        <LeftButton alignVerticallyToItemCards size="md" />
                        <CarouselContent>
                            {items?.map((item) => (
                                <ItemCard
                                    // This is to make the parent container set grid gaps instead
                                    // of individual cards keeping paddings and margins set of their own.
                                    // Makes displaying cards in different situations more predictable.
                                    className="p-0 m-0"
                                    item={item}
                                    key={item.itemId}
                                    onAddToWishlist={toggleWishList}
                                    onRemoveFromWishlist={toggleWishList}
                                    onQuickView={openQuickView}
                                    showFallback={!isLoading && !item.itemUrl}
                                    gaCategory="Startpage last search"
                                    showBidStatus
                                    showTitleOnOneLine
                                    showShippingOrOnDisplay={false}
                                />
                            ))}
                            {moreItemsCanBeFetched ? (
                                <MoreResultsCard
                                    href={toLocalizedUrl(
                                        `/search?${qs.stringify(queryParams)}`
                                    )}
                                    onClick={() =>
                                        trackContainerClick(
                                            'lastSearch',
                                            'Last search see all button'
                                        )
                                    }
                                    label={t(
                                        'startPage_savedSearches_moreResults'
                                    )}
                                />
                            ) : null}
                        </CarouselContent>
                        <RightButton alignVerticallyToItemCards size="md" />
                    </Carousel>
                </>
            )}
        </StartPageSection>
    );
};
